import React from "react";
import { Field, useField } from "react-final-form";
import { Box, FormHelperText, FormLabel, InputAdornment, Table, TableBody, TableContainer, TableHead, Typography } from "@mui/material";

import { CheckboxField } from "fond/form/fields";
import { NumericField } from "fond/form/fields/NumericField";
import { SECONDARY_WIRE_OPTIONS, SecondaryWireDescriptor } from "fond/project/panels/MuPower/architecture/constants";
import { atLeastOneEnabled, calculateMaxSecondaryLoadAmps, validateWhenEnabled } from "fond/project/panels/MuPower/architecture/helpers";
import { SystemOfMeasurement } from "fond/types";
import { formatNumber } from "fond/utils/number";
import { integer, requireNonZero } from "fond/utils/validation";

import { TableCell, TableHeadCell, TableRow } from "./MuPowerArchitectureModal.styles";

interface SecondaryWireTableRowProps {
  option: SecondaryWireDescriptor;
  index: number;
}

const SecondaryWireTableRow: React.FC<SecondaryWireTableRowProps> = ({ option, index }: SecondaryWireTableRowProps) => {
  const {
    input: { checked: enabled },
  } = useField(`Secondary.Options[${index}].Enabled`, { subscription: { value: true }, type: "checkbox" });
  const {
    input: { value: averageMeterLoadAmps },
  } = useField("Transformers.AverageMeterLoadAmps", { subscription: { value: true } });
  const {
    input: { value: averageAddressCount },
  } = useField(`Secondary.Options[${index}].AverageAddressCount`, { subscription: { value: true } });

  const maxSecondaryLoadAmps = calculateMaxSecondaryLoadAmps(averageMeterLoadAmps, averageAddressCount);
  const maxSecondaryLoadDisplay = maxSecondaryLoadAmps !== undefined ? `${formatNumber(maxSecondaryLoadAmps)} A` : "—";

  return (
    <TableRow data-testid={`secondary-wire-row-${option.Name}`}>
      <TableCell sx={{ pl: 1.5 }}>
        <CheckboxField
          name={`Secondary.Options[${index}].Enabled`}
          size="small"
          label={
            <Box>
              <Typography variant="body2">{option.Label}</Typography>
              <Typography variant="caption" color="text.secondary">
                {option.Description}
              </Typography>
            </Box>
          }
        />
      </TableCell>
      <TableCell>
        <NumericField
          name={`Secondary.Options[${index}].AverageAddressCount`}
          disabled={!enabled}
          margin="none"
          extraValidators={[validateWhenEnabled((values) => values.Secondary.Options[index].Enabled, integer)]}
        />
      </TableCell>
      <TableCell sx={{ px: 1.5 }}>
        <Typography variant="body2" color={enabled ? "text.primary" : "text.disabled"}>
          {enabled ? maxSecondaryLoadDisplay : "not available to design"}
        </Typography>
      </TableCell>
    </TableRow>
  );
};

interface SecondaryWiresTabProps {
  systemOfMeasurement: SystemOfMeasurement;
}

const SecondaryWiresTab: React.FC<SecondaryWiresTabProps> = ({ systemOfMeasurement }: SecondaryWiresTabProps) => (
  <>
    <Box>
      <FormLabel required>Secondary wire options</FormLabel>
      <Typography variant="caption" color="text.secondary" component="div">
        Conductors the design may use between transformer and meter. Capacity is average meter load multiplied by the average number of addresses fed.
      </Typography>
      <TableContainer sx={{ borderRadius: 1, border: (theme) => `1px solid ${theme.palette.divider}`, my: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow className="table-head-row">
              <TableHeadCell>
                <Typography>Conductor</Typography>
              </TableHeadCell>
              <TableHeadCell width={200}>
                <Typography>Average addresses fed</Typography>
              </TableHeadCell>
              <TableHeadCell width={200}>
                <Typography>Maximum secondary load</Typography>
              </TableHeadCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {SECONDARY_WIRE_OPTIONS.map((option, index) => (
              <SecondaryWireTableRow key={option.Name} option={option} index={index} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Field
        name="Secondary.OptionsGroup"
        validate={atLeastOneEnabled((values) => values.Secondary.Options, "Enable at least one secondary wire option")}
        render={({ meta }) => (meta.error && meta.submitFailed ? <FormHelperText error>{meta.error}</FormHelperText> : null)}
      />
    </Box>
    <Box mt={2}>
      <Box maxWidth={320}>
        <NumericField
          name="Secondary.MaxLength"
          label="Maximum secondary wire length"
          required
          min={0}
          extraValidators={[requireNonZero()]}
          InputProps={{ endAdornment: <InputAdornment position="end">{systemOfMeasurement === "imperial" ? "ft" : "m"}</InputAdornment> }}
        />
      </Box>
      <Typography variant="caption" color="text.secondary" component="div">
        Maximum wire length between a transformer and a meter location
      </Typography>
    </Box>
  </>
);

export default SecondaryWiresTab;
